import { detectLocale, initI18n, isLocale, type Locale } from '@akpl/i18n';
import type { i18n } from 'i18next';
import { useTranslation } from 'react-i18next';

export const LOCALE_KEY = 'akp.cms.locale';

function readOverride(): Locale | null {
  try {
    const raw = localStorage.getItem(LOCALE_KEY);
    return isLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Persisted override wins; otherwise the browser language (docs/01 §6: en + es only). */
export function resolveLocale(
  navigatorLanguage: string | undefined = typeof navigator === 'undefined'
    ? undefined
    : navigator.language,
): Locale {
  return readOverride() ?? detectLocale(navigatorLanguage);
}

let instance: i18n | null = null;

export async function initCmsI18n(locale: Locale = resolveLocale()): Promise<i18n> {
  if (instance) {
    if (instance.language !== locale) await instance.changeLanguage(locale);
    return instance;
  }
  instance = await initI18n({ lng: locale });
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
  return instance;
}

export function currentLocale(): Locale {
  const lng = instance?.language;
  return isLocale(lng) ? lng : resolveLocale();
}

export async function setLocale(locale: Locale): Promise<void> {
  try {
    localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    /* ignore */
  }
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
  if (instance) await instance.changeLanguage(locale);
}

/** `useTranslation` bound to the `cms` + `common` namespaces (typed against the bundle). */
export function useT() {
  return useTranslation(['cms', 'common']);
}
